import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, Outlet, ScrollRestoration, type RouteObject } from "react-router";
import { AuthProvider } from "@/auth/AuthProvider";
import { HomeRedirect, RedirectIfAuthed, RequireAdmin, RequireAuth } from "@/auth/guards";
import { AppShell } from "@/components/shell/AppShell";
import { RouteError } from "@/features/misc/RouteError";
import { NotFoundPage } from "@/features/misc/NotFoundPage";
import { MetaPixel } from "@/components/MetaPixel";
import { PageSkeleton } from "@/components/PageSkeleton";
import { useCart } from "@/features/commerce/cart";

const CartSheet = lazy(() => import("@/features/commerce/CartSheet"));

function GlobalCart() {
  const openFor = useCart((s) => s.openFor);
  if (!openFor) return null;
  return (
    <Suspense fallback={null}>
      <CartSheet siteId={openFor} />
    </Suspense>
  );
}

function RootLayout() {
  return (
    <AuthProvider>
      <MetaPixel />
      <Outlet />
      <GlobalCart />
      <ScrollRestoration />
    </AuthProvider>
  );
}

type PageModule = { default: React.ComponentType };
/** Lazy route with its own error boundary and a skeleton while the chunk loads. */
const page = (path: string | undefined, load: () => Promise<PageModule>, extra: Partial<RouteObject> = {}): RouteObject =>
  ({
    ...(path === undefined ? { index: true } : { path }),
    lazy: async () => ({ Component: (await load()).default }),
    errorElement: <RouteError />,
    HydrateFallback: PageSkeleton,
    ...extra,
  }) as RouteObject;

const siteTabs: RouteObject[] = [
  page(undefined, () => import("@/features/sites/overview/SummaryTab")),
  page("domain", () => import("@/features/sites/overview/DomainPlanTab")),
  page("statistik", () => import("@/features/sites/overview/AnalyticsTab")),
  page("riwayat", () => import("@/features/sites/overview/VersionsTab")),
  // Site orders live on "Domain & paket" now.
  { path: "pesanan", element: <Navigate to="../domain#pesanan" relative="path" replace /> },
  page("undangan", () => import("@/features/sites/overview/GuestLinksTab")),
  page("ucapan", () => import("@/features/sites/overview/ResponsesTab")),
  page("blog", () => import("@/features/sites/overview/BlogTab")),
  page("pengaturan", () => import("@/features/sites/overview/SettingsTab")),
];

const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <HomeRedirect /> },
      {
        element: <RedirectIfAuthed />,
        children: [
          page("masuk", () => import("@/features/auth/LoginPage")),
          page("daftar", () => import("@/features/auth/RegisterPage")),
          page("lupa-sandi", () => import("@/features/auth/ForgotPasswordPage")),
          page("atur-ulang-sandi", () => import("@/features/auth/ResetPasswordPage")),
          page("auth/reset-password", () => import("@/features/auth/ResetPasswordPage")),
        ],
      },
      page("verifikasi-email", () => import("@/features/auth/VerifyEmailPage")),
      page("auth/verify-email", () => import("@/features/auth/VerifyEmailPage")),
      page("auth/callback", () => import("@/features/auth/AuthCallbackPage")),
      page("buat", () => import("@/features/wizard/WizardPage")),
      page("promo/:code", () => import("@/features/commerce/ClaimPage")),
      {
        element: <RequireAuth />,
        children: [
          page("situs/:id/edit", () => import("@/features/editor/EditorPage")),
          page("situs/:id/pratinjau", () => import("@/features/preview/PreviewPage")),
          page("situs/:id/blog/:postId", () => import("@/features/blog/PostEditorPage")),
          page("pesanan/:orderId/kwitansi", () => import("@/features/commerce/ReceiptPage")),
          {
            element: <AppShell />,
            errorElement: <RouteError />,
            children: [
              page("situs", () => import("@/features/sites/SitesPage")),
              page("situs/:id/checkout", () => import("@/features/commerce/CheckoutPage")),
              page("situs/:id", () => import("@/features/sites/overview/SiteLayout"), { children: siteTabs }),
              page("pembayaran/:orderId", () => import("@/features/commerce/PaymentStatusPage")),
              page("pembayaran/:orderId/simulasi", () => import("@/features/commerce/PaymentSimulatorPage")),
              page("pesanan", () => import("@/features/commerce/OrdersPage")),
              page("template", () => import("@/features/library/LibraryPage")),
              page("akun", () => import("@/features/account/AccountPage")),
            ],
          },
        ],
      },
      {
        element: <RequireAdmin />,
        children: [page("admin/*", () => import("@/features/admin/AdminRoot"))],
      },
      ...(import.meta.env.DEV
        ? [
            page("dev/catalog", () => import("@/features/dev/CatalogGallery")),
            page("dev/catalog/:templateId/:designId?", () => import("@/features/dev/SitePreview")),
          ]
        : []),
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
